import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Upload, Loader2 } from "lucide-react";
import { COMPANY_DOC_CATEGORIES, WORKER_DOC_CATEGORIES, SITE_DOC_CATEGORIES, EQUIPMENT_DOC_CATEGORIES } from "../lib/utils-app";

const CATS = { company: COMPANY_DOC_CATEGORIES, worker: WORKER_DOC_CATEGORIES, site: SITE_DOC_CATEGORIES, equipment: EQUIPMENT_DOC_CATEGORIES };
const SECTIONS = [["company", "Azienda"], ["worker", "Lavoratore"], ["site", "Cantiere"], ["equipment", "Attrezzatura"]];
const inp = "mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg";

export default function EditDocumentDialog({ doc, onClose, onSaved }) {
  const [f, setF] = useState({ categoria: doc.categoria, nome: doc.nome, data_emissione: doc.data_emissione || "", data_scadenza: doc.data_scadenza || "",
    note: doc.note || "", entity_type: doc.entity_type, entity_id: doc.entity_id });
  const [ctx, setCtx] = useState({ workers: [], sites: [], equipment: [] });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/workers", { params: { company_id: doc.company_id } }),
      api.get("/sites", { params: { company_id: doc.company_id } }),
      api.get("/equipment", { params: { company_id: doc.company_id } }),
    ]).then(([w, s, e]) => setCtx({ workers: w.data, sites: s.data, equipment: e.data }));
  }, [doc.company_id]);

  const cats = CATS[f.entity_type] || [];
  const catKnown = cats.includes(f.categoria);
  const options = f.entity_type === "worker" ? ctx.workers.map(w => [w.id, `${w.cognome} ${w.nome}`])
    : f.entity_type === "site" ? ctx.sites.map(s => [s.id, s.denominazione])
    : f.entity_type === "equipment" ? ctx.equipment.map(e => [e.id, `${e.tipologia} ${e.matricola || ""}`]) : [];

  const submit = async (e) => {
    e.preventDefault();
    if (f.entity_type !== "company" && !f.entity_id) { toast.error("Seleziona la destinazione"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (file) fd.append("file", file);
      await api.put(`/documents/${doc.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento aggiornato");
      onSaved && onSaved(); onClose();
    } catch { toast.error("Errore aggiornamento"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-doc-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-lg">Modifica documento</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Sezione</label>
              <select value={f.entity_type} className={inp} data-testid="edit-doc-section"
                onChange={(e) => setF({ ...f, entity_type: e.target.value, entity_id: e.target.value === "company" ? doc.company_id : "" })}>
                {SECTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Destinazione</label>
              {f.entity_type === "company" ? <div className="mt-1 px-3 py-2 text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg">Documenti aziendali</div> : (
                <select value={f.entity_id} className={inp} data-testid="edit-doc-entity" onChange={(e) => setF({ ...f, entity_id: e.target.value })}>
                  <option value="">— seleziona —</option>
                  {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Categoria</label>
            <select value={catKnown ? f.categoria : "__custom__"} className={inp} data-testid="edit-doc-categoria"
              onChange={(e) => setF({ ...f, categoria: e.target.value === "__custom__" ? (catKnown ? "" : f.categoria) : e.target.value })}>
              {cats.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="__custom__">+ Categoria personalizzata</option>
            </select>
            {!catKnown && <input value={f.categoria} placeholder="Nome categoria" className={inp} onChange={(e) => setF({ ...f, categoria: e.target.value })} />}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Nome documento</label>
            <input value={f.nome} className={inp} data-testid="edit-doc-nome" onChange={(e) => setF({ ...f, nome: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-slate-700">Data emissione</label>
              <input type="date" value={f.data_emissione} className={inp} onChange={(e) => setF({ ...f, data_emissione: e.target.value })} /></div>
            <div><label className="text-sm font-medium text-slate-700">Data scadenza</label>
              <input type="date" value={f.data_scadenza} className={inp} data-testid="edit-doc-scadenza" onChange={(e) => setF({ ...f, data_scadenza: e.target.value })} /></div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Note</label>
            <textarea value={f.note} rows={2} className={inp} onChange={(e) => setF({ ...f, note: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Sostituisci file <span className="text-slate-400 font-normal">(opzionale)</span></label>
            <label className="dropzone mt-1 block">
              <input type="file" className="hidden" data-testid="edit-doc-file" onChange={(e) => setFile(e.target.files[0])} />
              <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-1">
                <Upload size={16} /> {file ? file.name : `Attuale: ${doc.original_filename}`}
              </div>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600">Annulla</button>
            <button type="submit" disabled={busy} data-testid="edit-doc-submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-60 inline-flex items-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
